import React from "react";
import styled from "styled-components";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import IssueChart, { ZOOMS } from "./Pivot";
import InfoTooltip from "../common/InfoTooltip";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import TimelineIcon from "@material-ui/icons/Timeline";
import IssueList from "./IssueList";
import CriticalityPill from "./CriticalityPill";
import { formatDay } from "./dates";
import { NOT_ATTRIBUTED } from "./checks";
import { defaultSinceOf } from "../IssueDeepDive/figures";
import { ISSUE_DEEP_DIVE_OPENED_FROM_ISSUES } from "../../reducers/issueDeepDive";


/** The URL without its host, as the crawler holds it: two URLs differing by a trailing slash are two pages here. */
const pathOf = (uri) => uri.replace(/^https?:\/\/[^/]+/, "") || uri;




const LOSS_HELP = "Estimated, not measured: on each measure, the page's average day in the 30 days before the issue started minus the 30 days from it, times 30. Only when Pettitt's test finds a drop on or after the start.";

const IMPORTANCE_HELP = "Estimated from the site's own links: how many clicks from the home page, and how many different link texts point at the page. No traffic data is linked.";

/** Each row's chart covers 3 months around its issues' start, whatever the date range. */
const SPAN = ZOOMS.find(({ key }) => key === "3m");

const compact = new Intl.NumberFormat("en-GB", { notation: "compact", maximumFractionDigits: 1 });

/**
 * One row per page and start day, with the issues that started that day; `issue` is the first of them, which carries the day's figures,
 * `criticalities` the row's level on each measure and `criticality` the highest. The loss column has a line per measure in `lossMeasures`;
 * the chart reads `measure`. `emptyText` is the one line shown when there are no rows.
 */
export default React.memo(function IssueRows({ rows, emptyText, chartDays, rankedPaths, lossMeasures, measure }) {
    const history = useHistory();
    const dispatch = useDispatch();
    const { workspaceNumber } = useParams();
    const trafficLost = lossMeasures.length > 0;

    return (
        <Table>
            <HeaderRow $traffic={trafficLost}>
                <span>Criticality</span>
                <span>Page</span>
                <span>Issues</span>
                {trafficLost && <HeaderLabel>Est. loss / month <InfoTooltip text={LOSS_HELP} /></HeaderLabel>}
                <span>{trafficLost ? `${measure.label} around the issue start` : "Page importance"}</span>
                <span />
            </HeaderRow>

            {rows.length === 0 && emptyText && <Empty>{emptyText}</Empty>}

            {rows.map((row) => (
                <Row key={row.page.id + row.issue.since} row={row} trafficLost={trafficLost} lossMeasures={lossMeasures} measure={measure} chartDays={chartDays} rankedPaths={rankedPaths}
                    workspaceNumber={workspaceNumber} history={history} dispatch={dispatch} />
            ))}
        </Table>
    );
});

const Row = React.memo(function Row({ row: { page, issues, issue, criticalities, criticality }, trafficLost, lossMeasures, measure, chartDays, rankedPaths, workspaceNumber, history, dispatch }) {
    const pageRoute = `/ws/${workspaceNumber}/monitored-uris/${page.id}`;
    // Without `?since=` the deep dive opens on the page's newest start; an issue never seen healthy has none.
    const since = issue.sinceKind !== "neverHealthy" && issue.since !== defaultSinceOf(page.issues) ? issue.since : null;
    const deepDive = { pathname: `/ws/${workspaceNumber}/issues/${page.id}`, search: since ? `?since=${since}` : "" };
    // A plain click keeps this list's location, so the deep dive's Back returns to it with its filters; a modified click opens a tab.
    const openDeepDive = (event) => {
        event.stopPropagation();
        if (event.ctrlKey || event.metaKey || event.button === 1) return;
        event.preventDefault();
        dispatch({
            type: ISSUE_DEEP_DIVE_OPENED_FROM_ISSUES,
            payload: { location: { pathname: history.location.pathname, search: history.location.search }, measure: measure.key },
        });
        history.push(deepDive);
    };
    // The row holds a link to the live page, and links can't nest, so the row opens a tab itself on Ctrl/Cmd or middle click.
    const open = (event) => {
        if (event.ctrlKey || event.metaKey || event.button === 1) window.open(history.createHref({ pathname: pageRoute }), "_blank");
        else history.push(pageRoute);
    };
    return (
        <Line $traffic={trafficLost} onClick={open} onAuxClick={(event) => event.button === 1 && open(event)}>
            {trafficLost ? <CriticalityPill criticality={criticality} criticalities={criticalities} /> : <span />}
            <PageCell title={page.uri}>
                <PathLine>
                    <Path>{pathOf(page.uri)}</Path>
                    <ExternalLink href={page.uri} target="_blank" rel="noopener noreferrer" title="Open in new tab" onClick={(event) => event.stopPropagation()}>
                        <OpenInNewIcon style={{ fontSize: "0.75rem" }} />
                    </ExternalLink>
                </PathLine>
                {page.lastChecked && <Checked>checked {formatDay(page.lastChecked)}</Checked>}
            </PageCell>

            <IssueList issues={issues} />

            {trafficLost && (
                <Traffic>
                    {lossMeasures.map(({ noun, readingOf }) => {
                        const reading = readingOf(issue);
                        return (
                            <div key={noun}>
                                {reading?.lost > 0
                                    ? <><Monthly>~{compact.format(Math.round(reading.lost * 30))}</Monthly> <Unit>{noun}</Unit></>
                                    : <Muted>{NOT_ATTRIBUTED[reading?.lossCheck] || "no loss"} · {noun}</Muted>}
                            </div>
                        );
                    })}
                </Traffic>
            )}

            <ChartCell>
                {trafficLost && <IssueChart since={issue.since} reading={measure.readingOf(issue)} spark={measure.spark(page)} days={chartDays} name={measure.chartLabel} around={SPAN.days} />}
                {!trafficLost && (
                    <Importance>
                        <Rank>#{page.pageWeight.toLocaleString()}</Rank>
                        <Muted> of {rankedPaths.toLocaleString()} · estimated</Muted>
                        <span onClick={(event) => event.stopPropagation()}><InfoTooltip text={IMPORTANCE_HELP} /></span>
                    </Importance>
                )}
            </ChartCell>

            <DeepDive href={history.createHref(deepDive)} onClick={openDeepDive} onAuxClick={(event) => event.stopPropagation()}>
                <TimelineIcon style={{ fontSize: "0.85rem" }} />
                Deep dive
            </DeepDive>
        </Line>
    );
});

const columns = ({ $traffic }) => [
    "112px",
    "minmax(0, 1.3fr)",
    "minmax(250px, 1.2fr)",
    $traffic && "160px",
    "minmax(0, 1fr)",
    "100px",
].filter(Boolean).join(" ");

const Table = styled.div`
    border: 1px solid #e8edf4;
    border-radius: 14px;
    background: #fff;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    @media (max-width: 600px) {
        border-radius: 0;
        border-left: none;
        border-right: none;
        box-shadow: none;
    }
`;

const HeaderRow = styled.div`
    display: grid;
    grid-template-columns: ${columns};
    align-items: center;
    gap: 20px;
    padding: 10px 18px;
    border-bottom: 1px solid #e4e7ec;
    background: #f5f6f899;
    font-size: 0.75rem;
    font-weight: 500;
    color: #5a6473;
    @media (max-width: 600px) { display: none; }
`;

const Line = styled.div`
    display: grid;
    grid-template-columns: ${columns};
    align-items: center;
    gap: 20px;
    padding: 9px 18px;
    border-bottom: 1px solid #f4f7fa;
    cursor: pointer;
    transition: background 120ms ease;
    &:last-child { border-bottom: none; }
    &:hover { background: #fbfcfe; }
    &:hover > div:nth-child(2) > div:first-child { color: #4f46e5; }
    @media (max-width: 600px) {
        grid-template-columns: minmax(0, 1fr);
        row-gap: 9px;
        padding: 14px 16px;
    }
`;

const Empty = styled.div`
    padding: 14px 18px;
    font-size: 0.8rem;
    color: #64748b;
    @media (max-width: 600px) { padding: 14px 16px; }
`;

const PageCell = styled.div`
    min-width: 0;
`;

const PathLine = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
`;

const Path = styled.div`
    min-width: 0;
    font-size: 0.86rem;
    font-weight: 600;
    letter-spacing: -0.1px;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: color 120ms ease;
`;

const ExternalLink = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    border-radius: 4px;
    color: #cbd5e1;
    text-decoration: none;
    transition: background 120ms, color 120ms;
    &:hover { background: #ede9fe; color: #6366f1; }
`;

const DeepDive = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    justify-self: end;
    padding: 4px 10px 4px 8px;
    border: 1px solid #c7d2fe;
    border-radius: 999px;
    background: #eef2ff;
    font-size: 0.7rem;
    font-weight: 600;
    color: #4f46e5;
    white-space: nowrap;
    text-decoration: none;
    transition: background 120ms, border-color 120ms, color 120ms;
    &:hover { background: #4f46e5; border-color: #4f46e5; color: #fff; text-decoration: none; }
`;

const Checked = styled.div`
    margin-top: 3px;
    font-size: 0.68rem;
    color: #a9b4c2;
`;

const Muted = styled.span`
    font-size: 0.7rem;
    font-weight: 400;
    color: #a9b4c2;
`;

const ChartCell = styled.div`
    min-width: 0;
`;

const Traffic = styled.div`
    padding-top: 1px;
    white-space: nowrap;
`;

const HeaderLabel = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
`;


const Unit = styled.span`
    font-size: 0.72rem;
    color: #64748b;
`;

const Monthly = styled.span`
    font-size: 0.86rem;
    font-weight: 700;
    color: #0f172a;
`;

const Importance = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const Rank = styled.span`
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
`;
